// TODO: Define (copy from FlowsEditorTypes) shared "flow" types

import { FlowReelStat } from './flowReelStat';
import { Team } from './team.type';

export type Flow = {
    id: number;
    organisation_id: number;
    team_id: number;
    number: number;
    status: FlowStatus;
    name: string;
    created_at: string;
    created_by_name: string | null;
    updated_at: string;
    updated_by_name: string | null;
    deleted_at: string;
    published_at: string;
    published_by_name: string | null;
    unpublished_at: string | null;
    unpublished_by_name: string | null;
    archived_at: string | null;
    unarchived_at: string | null;
    // NOTE: The value can be null for old flows (until backfill command has been run)
    first_published_at: string | null;
    first_published_by_name: string | null;
    config: FlowConfig | FlowConfig_V2;
    team: Team;
    code: string;
    has_newer_version: boolean;
    flow_reel_stats?: FlowReelStat[];
    eye_catcher_clicks?: number;
    latest_version_created_by_name: string | null;
    latest_version_created_at: string | null;
    first_reel_screenshot_url?: string | null;
};

export const DRAFT_FLOW_STATUS = 'draft';
export const PUBLISHED_FLOW_STATUS = 'published';
export const UNPUBLISHED_FLOW_STATUS = 'unpublished';

const flowStatuses = [
    DRAFT_FLOW_STATUS,
    PUBLISHED_FLOW_STATUS,
    UNPUBLISHED_FLOW_STATUS,
] as const;

export type FlowStatus = (typeof flowStatuses)[number];

export type FlowConfig = {
    configVersion?: 1;
    page: string;
    pageMatchVariant?: 1 | 2;
    targetNodeSelector?: string | null;
    objectives?: string[] | null;
    type: FlowConfigType;
    // NOTE: The eye catcher may not be present (e.g. the "inline" type of the player)
    eyeCatcher?: FlowConfigEyeCatcher;
    player: FlowConfigPlayer<FlowConfigPlayerVideo>;
};

export type FlowConfig_V2 = Omit<
    Omit<Omit<FlowConfig, 'eyeCatcher'>, 'player'>,
    'configVersion'
> & {
    configVersion: 2;
    eyeCatcher?: FlowConfigEyeCatcher_V2;
    player: FlowConfigPlayer<FlowConfigPlayerVideo_V2>;
};

export type FlowConfigType = 'overlay' | 'inline';

export type FlowConfigEyeCatcher = {
    style?: Record<string, Record<string, string>>;
    title: string | { default: string; template: string };
    playbackRate?: number;
    // NOTE: Defaults to `true` when not present
    enabled?: boolean;
    presentationDelay?: number;
    presentationScrollOffset?: number;
    urlMap: {
        /**
         * @deprecated Not in use anymore, will be removed in future versions
         */
        gif: string;
        mp4: string;
        vtt?: string;
    };
};

export type FlowConfigEyeCatcher_V2 = Omit<FlowConfigEyeCatcher, 'urlMap'> & {
    // ...
};

export type FlowConfigPlayer<T> = {
    style?: Record<string, Record<string, string>>;
    videos: T[];
    autoPlay?: boolean;
    autoPlayDelay?: number;
    brandingVariant?: number;
    themeVariant?: number;
    sizeVariant?: number;
};

export type FlowConfigPlayerVideo = {
    id: string;
    url: string;
    subtitles: string;
    authorName: string;
    authorRole: string;
    text: string | { default: string; template: string };
    // NOTE: Unset (`undefined`/`null`) means the user hasn't touched the title yet, so it follows
    //   the incoming button label (see `syncStepTitleWithButtonLabel`). An empty string is a user choice.
    title?: string | { default: string; template: string } | null;
    buttonPresentationDelay?: number;
    buttons: FlowConfigPlayerVideoButton[];
    hasError?: boolean;
};

export type FlowConfigPlayerVideo_V2 = Omit<
    Omit<FlowConfigPlayerVideo, 'url'>,
    'subtitles'
> & {
    // NOTE: The "reelId" is a reel code
    reelId: string;
    notes?: string;
};

export const OPEN_VIDEO_ACTION_TYPE = 'custom:open_video_button';
export const HIDE_PLAYER_ACTION_TYPE = 'custom:hide_player_button';
export const START_CHAT_ACTION_TYPE = 'custom:start_chat_button';

// NOTE: Label of a newly added button. Never shown as a step title (see `Flow::DEFAULT_BUTTON_LABEL`)
export const DEFAULT_BUTTON_LABEL = 'New Button';

export const actionTypeList = [
    OPEN_VIDEO_ACTION_TYPE,
    HIDE_PLAYER_ACTION_TYPE,
    START_CHAT_ACTION_TYPE,
] as const;

export type ActionType = (typeof actionTypeList)[number];

export type StartChatActionPayload = {
    chatService: ChatService;
    hideOnOpen: boolean;
    showAfterEnd: boolean;
};

export type FlowConfigPlayerVideoButton = {
    id: string;
    label: string;
    actionType: ActionType | string;

    // actionPayload shape varies by actionType:
    // - OPEN_VIDEO_ACTION_TYPE:   { videoId: string }
    // - 'a' (redirect):          { href: string; target: string; hidePlayerOnNavigate?: string }
    // - HIDE_PLAYER_ACTION_TYPE: {}
    // - START_CHAT_ACTION_TYPE:  { chatService: ChatService; hideOnOpen: boolean; showAfterEnd: boolean }

    actionPayload: Record<string, string | boolean>;
    style?: Record<string, string>;
};

export const OPEN_PAGE_STEP_TYPE = 'step-type/open-page';
export const SCROLL_TO_SECTION_STEP_TYPE = 'step-type/scroll-to-section';
export const REEL_STEP_TYPE = 'step-type/reel';
export const SCROLL_TO_SECTION_AND_PLAY_REEL_STEP_TYPE =
    'step-type/scroll-to-section-and-play-reel';
export const CLOSE_PLAYER_STEP_TYPE = 'step-type/close-player';
export const START_CHAT_STEP_TYPE = 'step-type/start-chat';

export const stepTypeList = [
    OPEN_PAGE_STEP_TYPE,
    SCROLL_TO_SECTION_STEP_TYPE,
    REEL_STEP_TYPE,
    SCROLL_TO_SECTION_AND_PLAY_REEL_STEP_TYPE,
    CLOSE_PLAYER_STEP_TYPE,
    START_CHAT_STEP_TYPE,
] as const;

export const INTERCOM_CHAT_SERVICE = 'intercom';
export const HUBSPOT_CHAT_SERVICE = 'hubspot';
export const CHATBASE_CHAT_SERVICE = 'chatbase';
export const ZAPIER_CHAT_SERVICE = 'zapier';
export const SALESFORCE_CHAT_SERVICE = 'salesforce';
export const CUSTOM_CHAT_SERVICE = 'custom';

export const chatServiceList = [
    INTERCOM_CHAT_SERVICE,
    HUBSPOT_CHAT_SERVICE,
    CHATBASE_CHAT_SERVICE,
    ZAPIER_CHAT_SERVICE,
    SALESFORCE_CHAT_SERVICE,
    CUSTOM_CHAT_SERVICE,
] as const;

export type ChatService = (typeof chatServiceList)[number];

export type StepType = (typeof stepTypeList)[number];

export const AUTOSAVE_RECENTLY_SAVED = 'recently';
export const AUTOSAVE_PENDING = 'pending';
export const AUTOSAVE_SAVING = 'saving';
export const AUTOSAVE_SAVED = 'saved';
export const AUTOSAVE_ERROR = 'error';

export const autosaveStatusList = [
    AUTOSAVE_PENDING,
    AUTOSAVE_SAVING,
    AUTOSAVE_SAVED,
    AUTOSAVE_RECENTLY_SAVED,
    AUTOSAVE_ERROR,
] as const;

export type AutosaveStatus = (typeof autosaveStatusList)[number];
